/**
 * ActivityStrip — the Command Center's lower row (mockup 01): "Recent
 * changes" as a Carbon structured list beside the 90-day trend per pillar.
 * The two tiles stack below 1056px.
 *
 * Recent changes: DashboardStats.recentActivity (existing endpoint), the six
 * newest events with a time, the event, a helper line and a tag naming the
 * kind of change. A new drift takes the high-contrast neutral tag; nothing
 * here carries a hue (the Neutral Bad State Rule). §7 C26: "Recent changes",
 * not "since last week" (the feed is capped, not a calendar week).
 *
 * The trend: the daily Guardian Score components over the 90-day window
 * (§7 C28; composeScoreHistory in cc-history.ts, sampled by cc-trend.ts),
 * one line per component in its category mark, Secure Score dashed. A tenant
 * with under a week of history gets a short-history note; an empty history
 * says so, never a flat line of zeros.
 */

import { useMemo } from 'react'
import { Skeleton, StructuredList, Tag, Tile, TrendChart, type StructuredListRow } from '@/components/carbon'
import type { DashboardStats } from '@/types'
import type { ExecutiveReportData, ScoreDataPoint } from '@/types/executiveReport'
import { changeSub, changeTag, changeTime, changesRange } from './cc-changes'
import { HISTORY_DAYS, pillarTrend, SHORT_HISTORY_DAYS, trendSubtitle } from './cc-trend'

interface Props {
  stats: DashboardStats | null | undefined
  isLoading: boolean
  /** The daily rows behind the trend tile (composeScoreHistory, 90-day window). */
  trendPoints?: ReadonlyArray<ScoreDataPoint> | null
  /** A history source is still loading. */
  trendLoading?: boolean
  /** Legacy: an executive report whose trend feeds the tile when `trendPoints` is absent. The Command Center no longer passes it. */
  report?: ExecutiveReportData | null
  reportLoading?: boolean
  /** The report query failed. */
  reportError?: boolean
}

const SHOWN = 6

export default function ActivityStrip({
  stats,
  isLoading,
  trendPoints,
  trendLoading = false,
  report,
  reportLoading = false,
  reportError = false,
}: Props) {
  const events = useMemo(() => (stats?.recentActivity ?? []).slice(0, SHOWN), [stats?.recentActivity])
  const rows = useMemo<StructuredListRow[]>(() => {
    const now = new Date()
    return events.map(e => {
      const tag = changeTag(e.type)
      return {
        id: e.id,
        time: changeTime(e.timestamp, now),
        title: e.title,
        sub: changeSub(e),
        tag: <Tag kind={tag.kind}>{tag.label}</Tag>,
      }
    })
  }, [events])
  const range = changesRange(events)

  const legacy = trendPoints === undefined
  const trend = useMemo(
    () => pillarTrend(legacy ? report?.guardianScoreTrend : trendPoints),
    [legacy, report?.guardianScoreTrend, trendPoints],
  )
  const subtitle = trendSubtitle(trend)
  const period = legacy ? report?.reportPeriodDays ?? HISTORY_DAYS : HISTORY_DAYS
  const trendBusy = legacy ? reportLoading : trendLoading
  const trendFailed = legacy && reportError

  return (
    <div className="mt-12 grid grid-cols-1 gap-8 c-lg:grid-cols-2">
      <Tile as="section" aria-labelledby="cc-changes-heading" className="min-w-0 pb-6">
        <h2 id="cc-changes-heading" className="m-0 text-heading font-normal text-text-primary">
          Recent changes
        </h2>
        {range ? <p className="m-0 mt-0.5 text-helper text-text-helper">{range}</p> : null}
        {isLoading ? (
          <Skeleton lines={4} label="Loading recent changes" className="mt-4" />
        ) : (
          <StructuredList
            rows={rows}
            aria-label="Recent changes"
            empty="No changes recorded yet."
            className="mt-4"
          />
        )}
      </Tile>

      <Tile as="section" aria-labelledby="cc-trend-heading" className="min-w-0 pb-6">
        <h2 id="cc-trend-heading" className="m-0 text-heading font-normal text-text-primary">
          Last {period} days
        </h2>
        {subtitle ? <p className="m-0 mt-0.5 text-helper text-text-helper">{subtitle}</p> : null}
        {trendBusy ? (
          <Skeleton lines={4} label="Loading the trend" className="mt-4" />
        ) : trendFailed ? (
          <p className="m-0 mt-4 border-t border-line-subtle py-6 text-helper text-text-helper">
            The trend did not load. Refresh the page to try again.
          </p>
        ) : trend.series.length === 0 ? (
          <p className="m-0 mt-4 border-t border-line-subtle py-6 text-helper text-text-helper">
            The trend fills in as Guardian records scores for this tenant.
          </p>
        ) : (
          <>
            {trend.days < SHORT_HISTORY_DAYS ? (
              <p className="m-0 mt-2 text-helper text-text-helper">
                This tenant has {trend.days} {trend.days === 1 ? 'day' : 'days'} of history so far.
              </p>
            ) : null}
            <TrendChart series={trend.series} xDomain={trend.xDomain} yDomain={trend.yDomain} className="mt-4" />
          </>
        )}
      </Tile>
    </div>
  )
}
